import PropTypes from 'prop-types'
import { useRef, useEffect } from 'react'
import useSlugs from '../../../hooks/useSlugs'
import { EnvConfig } from '../../../config/EnvConfig'
import { getWebsiteUrl, useAppPathPrefix } from '../../../utils/getWebsiteUrl'
import useDeviceDetect from '../../../hooks/useDeviceDetect'
import { isMobileApp } from '../../../utils/mobileAppFunctionality'

const websiteURL = getWebsiteUrl()
const locale = EnvConfig.GATSBY_INTL_LOCALE

export const KambiScript = ({ pathname }) => {
  const isMountedRef = useRef(false)
  const { isMobile } = useDeviceDetect()

  const getRouteRoot = () => {
    if (pathname.includes(`/${locale}/`)) {
      const [, localeParam, path] = pathname.split('/')
      return `${localeParam}/${path}`
    }

    if (useAppPathPrefix) {
      const [, appPath, sportsPath] = pathname.split('/')
      return `${appPath}/${sportsPath}`
    }

    if (useAppPathPrefix && pathname.includes(`/${locale}/`)) {
      const [, appPath, sportsPath, localePath] = pathname.split('/')
      return `${appPath}/${sportsPath}/${localePath}`
    }

    return pathname.split('/')?.[1] || ''
  }
  
  const routeRoot = getRouteRoot()
  const [loginSlug, depositSlug] = useSlugs(['login', 'deposit'])
  const loginRoute = `${websiteURL}${loginSlug}`
  const depositRoute = `${websiteURL}${depositSlug}`

  const handleNotificationEvent = (data) => {
    const event = new CustomEvent('kambi-notification', { detail: data })
    window.dispatchEvent(event)
  }

  if (!loginSlug) return null

  // Ensure that the script only runs once
  useEffect(() => {
    if (isMountedRef.current) return

    if (isMobile === null) return

    isMountedRef.current = true

    const getBetslipQuerySelectors = () => {
      if (isMobileApp()) return '{}'

      if (isMobile) {
        return `{
          'pinned': '',
          'unpinned': '',
          'unpinnedV2': '#bottom-anchor'
        }`
      }

      return '{}'
    }

    const betslipQuerySelectors = getBetslipQuerySelectors()

    // Append the script to the DOM
    const scriptContent = `
      (function (global) {
        const CustomerSettings = function () {
          this.enablePushState = true
          this.routeRoot = '${routeRoot}'
          this.loginUrl = '${loginRoute}'
          this.depositUrl = '${depositRoute}'
          this.useHumanReadableUrls = true
          this.betslipQuerySelectors = ${betslipQuerySelectors}
        }
        CustomerSettings.prototype.notification = ${handleNotificationEvent.toString()}
        global.customerSettings = new CustomerSettings()
      }(window))`

    const scriptElement = document.createElement('script')
    scriptElement.innerHTML = scriptContent
    document.body.appendChild(scriptElement)

    // Cleanup to remove script if necessary (optional)
    return () => {
      isMountedRef.current = false
      if (scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement)
      }
    }
  }, [routeRoot, loginRoute, depositRoute, isMobile])

  // No need to return a <Script> component since the script is managed by useEffect
  return null
}

KambiScript.defaultProps = {
  pathname: '',
}

KambiScript.propTypes = {
  pathname: PropTypes.string,
}